import { useRef, forwardRef, useCallback, useLayoutEffect } from 'react';
import type { FunctionComponent, PropsWithoutRef, Ref } from 'react';
import styled, { css } from 'styled-components';

import { useConsolidatedRef, useFocusWithin, useI18n, useTestIds, useUID } from '../../../hooks';
import type { ForwardProps } from '../../../types';
import { defaultThemeProp } from '../../../theme';
import type { FormControlProps } from '../../FormControl';
import Flex from '../../Flex';
import FormField, { StyledFormField } from '../../FormField/FormField';
import DateRangePicker from '../Picker/DateRangePicker';
import type DateTimeProps from '../DateTime.types';
import type {
  DateRangeCallbackParameter,
  DateTimeCallbackParameter,
  DateTimeProperties
} from '../DateTime.types';
import { StyledRangeInputContainer } from '../../Input/Input.styles';
import { getDateRangeInputTestIds } from '../DateTime.test-ids';
import { createClassName, withTestIds } from '../../../utils';
import HiddenText from '../../HiddenText';

import DateInput from './DateInput';
import type { DateInputHandleValue } from './DateInput';
import { parseToDate } from './utils';
import StyledDateInput from './DateTime.styles';

export interface DateRangeInputProps extends DateTimeProperties {
  /** Object of range boundaries. */
  value?: { start?: DateTimeProps['value']; end?: DateTimeProps['value'] };
  /**
   * Display additional column with week number in date picker popover.
   * @default false
   */
  showWeekNumber?: boolean;
  /** Pass a string or a fragment with an Icon and string for first part of DataRangeInput. */
  fromLabel?: FormControlProps['label'];
  /** Pass a string or a fragment with an Icon and string for second part of DataRangeInput. */
  toLabel?: FormControlProps['label'];
  /** Pass an object with "heading" and "content" properties to show AdditionalInfo for second part of DataRangeInput. */
  toAdditionalInfo?: FormControlProps['additionalInfo'];
  /** Pass an object with "heading" and "content" properties to show AdditionalInfo for first part of DataRangeInput. */
  fromAdditionalInfo?: FormControlProps['additionalInfo'];
  /** Callback fired when input value changes. */
  onChange: (value: DateRangeCallbackParameter) => void;
  /** Callback fired when the component gets focused. */
  onFocus?: (value: DateRangeCallbackParameter) => void;
  /** Callback fired when the component loses focus. */
  onBlur?: (value: DateRangeCallbackParameter) => void;
  /** Automatically focuses the input on render. */
  autoFocus?: boolean;
}

const StyledDateRangeInput = styled.fieldset<DateRangeInputProps>(props => {
  const {
    theme: {
      components: { input }
    }
  } = props;

  return css`
    border: 0;
    min-width: min-content;

    ${StyledFormField} {
      &:last-child > ${StyledDateInput} {
        margin-inline-start: -${input['border-width']};
      }

      & legend {
        align-self: center;
      }

      &:focus,
      &:hover,
      &:focus-within {
        z-index: 2;
      }
    }
  `;
});

StyledDateRangeInput.defaultProps = defaultThemeProp;

type DateRepresentable = string | number | Date;

const convertToCallbackParameter = (v?: number): DateTimeCallbackParameter => {
  return {
    valueAsISOString: v === undefined || Number.isNaN(v) ? undefined : new Date(v).toISOString(),
    valueAsTimestamp: v
  };
};

const prepareValue = (v: number | undefined, type: 'start' | 'end') => {
  if (v === undefined || Number.isNaN(v)) {
    return v;
  }
  return type === 'start'
    ? new Date(v).setUTCHours(0, 0, 0, 0)
    : new Date(v).setUTCHours(23, 59, 59, 999);
};

const getSelected = (first: number | undefined, second: number | undefined) => {
  const selected = first ?? second;
  return selected ? parseToDate(selected) : undefined;
};

const parseValueToDate = (value: DateRepresentable | undefined) => {
  return value ? parseToDate(value) : undefined;
};

const DateRangeInput: FunctionComponent<DateRangeInputProps & ForwardProps> = forwardRef(
  function DateRangeInput(
    {
      testId,
      value = {},
      min,
      max,
      label,
      fromLabel,
      toLabel,
      labelHidden,
      status,
      required,
      readOnly,
      disabled,
      onChange,
      onFocus,
      onBlur,
      showWeekNumber,
      timeZone,
      autoFocus,
      fromAdditionalInfo,
      toAdditionalInfo,
      className,
      ...restProps
    }: PropsWithoutRef<DateRangeInputProps>,
    ref: Ref<HTMLDivElement>
  ) {
    const testIds = useTestIds(testId, getDateRangeInputTestIds);
    const t = useI18n();
    const endDateHandle = useRef<DateInputHandleValue>(null);

    const start =
      value.start === undefined || value.start === ''
        ? undefined
        : prepareValue(parseToDate(value.start).getTime(), 'start');
    const end =
      value.end === undefined || value.end === ''
        ? undefined
        : prepareValue(parseToDate(value.end).getTime(), 'end');

    const startDateRef = useRef<DateTimeCallbackParameter>(convertToCallbackParameter(start));
    const endDateRef = useRef<DateTimeCallbackParameter>(convertToCallbackParameter(end));

    useLayoutEffect(() => {
      startDateRef.current = convertToCallbackParameter(start);
      endDateRef.current = convertToCallbackParameter(end);
    }, [start, end]);

    const onFocusChange = useCallback(
      (focused: boolean) => {
        (focused ? onFocus : onBlur)?.({
          start: startDateRef.current,
          end: endDateRef.current
        });
      },
      [onFocus, onBlur]
    );

    const containerRef = useConsolidatedRef(ref);
    useFocusWithin([containerRef], onFocusChange);

    const onStartDateChange = (selectedDate?: Date) => {
      const selected = selectedDate?.getTime();
      if (end && selected && selected > end) {
        startDateRef.current = convertToCallbackParameter(prepareValue(end, 'start'));
        endDateRef.current = convertToCallbackParameter(prepareValue(selected, 'end'));
        onChange({
          start: convertToCallbackParameter(prepareValue(end, 'start')),
          end: convertToCallbackParameter(prepareValue(selected, 'end'))
        });
      } else {
        startDateRef.current = convertToCallbackParameter(prepareValue(selected, 'start'));
        endDateRef.current = convertToCallbackParameter(end);
        onChange({
          start: convertToCallbackParameter(prepareValue(selected, 'start')),
          end: convertToCallbackParameter(end)
        });
      }
      endDateHandle.current?.setOpen(true);
    };

    const onEndDateChange = (selectedDate?: Date) => {
      const selected = selectedDate?.getTime();
      if (start && selected && selected < start) {
        endDateRef.current = convertToCallbackParameter(prepareValue(start, 'end'));
        startDateRef.current = convertToCallbackParameter(prepareValue(selected, 'start'));
        onChange({
          end: convertToCallbackParameter(prepareValue(start, 'end')),
          start: convertToCallbackParameter(prepareValue(selected, 'start'))
        });
      } else {
        endDateRef.current = convertToCallbackParameter(prepareValue(selected, 'end'));
        startDateRef.current = convertToCallbackParameter(start);
        onChange({
          start: convertToCallbackParameter(start),
          end: convertToCallbackParameter(prepareValue(selected, 'end'))
        });
      }
    };

    const getSelectedRange = () => {
      const startDate = parseValueToDate(start);
      const endDate = parseValueToDate(end);

      if (startDate && endDate && startDate > endDate) return undefined;

      return {
        startDate,
        endDate
      };
    };

    const commonProps = {
      required,
      readOnly,
      disabled,
      min,
      max,
      status,
      showWeekNumber,
      timeZone
    };

    const descriptionId = useUID();

    return (
      <>
        <FormField
          testId={testIds}
          as={StyledDateRangeInput}
          {...restProps}
          ref={containerRef}
          container={{ wrap: 'nowrap' }}
          labelHidden={labelHidden}
          label={label}
          required={required}
          readOnly={readOnly}
          status={status}
          disabled={disabled}
          aria-describedby={descriptionId}
        >
          <Flex
            container
            as={StyledRangeInputContainer}
            className={createClassName('date-range-input', className, { status })}
          >
            <DateInput
              testId={testIds.fromDate}
              {...commonProps}
              value={value.start}
              picker={{
                renderer: DateRangePicker,
                rendererProps: {
                  selected: getSelected(start, end),
                  selectedRange: getSelectedRange(),
                  onChange: onStartDateChange
                }
              }}
              onBlur={changedValue => {
                const newValue = changedValue.state
                  ? changedValue
                  : convertToCallbackParameter(
                      prepareValue(changedValue.valueAsTimestamp, 'start')
                    );
                startDateRef.current = newValue;
                onChange({
                  start: newValue,
                  end: convertToCallbackParameter(prepareValue(end, 'end'))
                });
              }}
              additionalInfo={fromAdditionalInfo}
              label={fromLabel}
              aria-label={`${t('range_from_datetime') + (label ? ` - ${label}` : '')}`}
              autoFocus={autoFocus}
            />
            <DateInput
              testId={testIds.toDate}
              {...commonProps}
              value={value.end}
              handle={endDateHandle}
              picker={{
                renderer: DateRangePicker,
                rendererProps: {
                  selected: getSelected(end, start),
                  selectedRange: getSelectedRange(),
                  onChange: onEndDateChange
                }
              }}
              onBlur={changedValue => {
                const newValue = changedValue.state
                  ? changedValue
                  : convertToCallbackParameter(prepareValue(changedValue.valueAsTimestamp, 'end'));
                endDateRef.current = newValue;
                onChange({
                  start: convertToCallbackParameter(prepareValue(start, 'start')),
                  end: newValue
                });
              }}
              additionalInfo={toAdditionalInfo}
              label={toLabel}
              aria-label={`${t('range_to_datetime') + (label ? ` - ${label}` : '')}`}
            />
          </Flex>
        </FormField>
        <HiddenText id={descriptionId}>{t('range_input')}</HiddenText>
      </>
    );
  }
);

export default withTestIds(DateRangeInput, getDateRangeInputTestIds);
